import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import adminService from '../../services/adminService';
import '../../styles/components/admin/AdminLayout.css';
import '../../styles/components/admin/UserManagement.css';

import SearchIcon from '../../assets/icons/search_icon.svg';
import EditIcon from '../../assets/icons/edit_icon.svg';
import TrashIcon from '../../assets/icons/delete_icon.svg';
import EyeIcon from '../../assets/icons/view_icon.svg';
import DownloadIcon from '../../assets/icons/appointment_icon.svg';
import CheckCircleIcon from '../../assets/icons/view_icon.svg';
import XCircleIcon from '../../assets/icons/DoctorManagement_Icon.svg';

import leftArrow from '../../assets/icons/indicators/next_left_icon.svg';
import rightArrow from '../../assets/icons/indicators/next_right_icon.svg';
import PdfIcon from '../../assets/icons/indicators/EXPORT_PDF.svg';

import systemLogo from '../../assets/images/Neurolex_Logo_New.png'; 

const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, patientName, patientEmail }) => {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="user-management-modal-header">
          <div className="user-management-logo-modal-logo">
            <img src={systemLogo} alt="System Logo" className="user-management-logo-modal-logo" />
          </div>
        </div>
        
        <div className="user-management-modal-content">
          <h2 className="user-management-modal-title">DELETE PATIENT ACCOUNT?</h2>
          
          <div className="patient-info-card">
            <div className="admin-user-mana-patient-name">{patientName}</div>
            <div className="admin-user-mana-patient-email">{patientEmail}</div>
          </div>
          
          <p className="admin-user-mana-modal-message">
            Delete this user account? This is permanent and can’t be undone, 
            but it’s okay to let go if you're ready.
          </p>
          
          <div className="admin-user-mana-modal-actions">
            <button className="admin-user-mana-modal-cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button className="admin-user-mana-modal-delete-btn" onClick={onConfirm}>
              Yes, Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const PatientManagement = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [genderFilter, setGenderFilter] = useState('All');
  const [tenantFilter, setTenantFilter] = useState('All');
  const [tenants, setTenants] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    patientId: null,
    tenantId: null,
    patientName: '',
    patientEmail: ''
  });
  
  const navigate = useNavigate();

  const fetchTenants = useCallback(async () => {
    try {
      const response = await adminService.getTenants();
      if (response.success) {
        setTenants(response.data);
      }
    } catch (err) {
      console.error('Error fetching tenants:', err);
    }
  }, []);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminService.getAllPatients({
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm || undefined,
        status: statusFilter !== 'All' ? statusFilter.toLowerCase() : undefined,
        gender: genderFilter !== 'All' ? genderFilter : undefined,
        tenantId: tenantFilter !== 'All' ? tenantFilter : undefined,
      });
      
      if (response.success) {
        setPatients(response.data);
        if (response.pagination) {
          setTotalPages(response.pagination.pages);
        }
      } else {
        throw new Error(response.message || 'Failed to fetch patients');
      }
    } catch (err) {
      console.error('Error fetching patients:', err);
      setError(err.message || 'An error occurred while fetching patients');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, statusFilter, genderFilter, tenantFilter]);

  useEffect(() => {
    fetchTenants();
  }, [fetchTenants]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const handleDeletePatient = (patientId, tenantId, patientName, patientEmail) => {
    setDeleteModal({
      isOpen: true,
      patientId,
      tenantId,
      patientName,
      patientEmail
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      patientId: null,
      tenantId: null,
      patientName: '',
      patientEmail: ''
    });
  };

  const confirmDeletePatient = async () => {
    try {
      const response = await adminService.deletePatient(deleteModal.patientId, deleteModal.tenantId);
      
      if (response.success) {
        setPatients(prevPatients => prevPatients.filter(patient => patient._id !== deleteModal.patientId));
        alert('Patient deleted successfully');
        closeDeleteModal();
      } else {
        throw new Error(response.message || 'Failed to delete patient');
      }
    } catch (err) {
      console.error('Error deleting patient:', err);
      alert(`Error: ${err.message || 'An error occurred while deleting the patient'}`);
      closeDeleteModal();
    }
  };

  const handleExportPDF = async () => {
    try {
      const filters = {
        search: searchTerm || undefined,
        status: statusFilter !== 'All' ? statusFilter.toLowerCase() : undefined,
        gender: genderFilter !== 'All' ? genderFilter : undefined,
        tenantId: tenantFilter !== 'All' ? tenantFilter : undefined
      };
      
      await adminService.exportPatientsToPdf(filters);
      
    } catch (err) {
      console.error('Error exporting PDF:', err);
      alert(`Error: ${err.message || 'An error occurred while exporting to PDF'}`);
    }
  };

  const handleAddPatient = () => {
    navigate('/admin/patients/add');
  };

  const handleViewPatient = (patientId, tenantId) => {
    navigate(`/admin/patients/${patientId}?tenantId=${tenantId}`);
  };

  const handleEditPatient = (patientId, tenantId) => {
    navigate(`/admin/patients/${patientId}/edit?tenantId=${tenantId}`);
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getTenantName = (tenantId) => {
    const tenant = tenants.find(t => t._id === tenantId);
    return tenant ? tenant.name : 'Unknown Clinic';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="pm-status-badge pm-active">
            <span className="pm-status-dot pm-dot-active"></span> Active
          </span>
        );
      case 'pending':
        return (
          <span className="pm-status-badge pm-pending">
            <span className="pm-status-dot pm-dot-pending"></span> Pending
          </span>
        );
      case 'inactive':
      case 'suspended':
        return (
          <span className="pm-status-badge pm-inactive">
            <span className="pm-status-dot pm-dot-inactive"></span> Inactive
          </span>
        );
      default:
        return <span className="pm-status-badge">{status || 'Unknown'}</span>;
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="loading-spinner"></div></div>;
  }

  if (error) {
    return <div className="error-alert"><span className="alert-title">Error:</span> {error}</div>;
  }

  return (
    <div className="admin-content">
      <div className="user-management-header">
        <h1>Patient Management</h1>
        <p>Easily view, update, and manage all patients in the system.</p>
      </div>

      <div className="pm-admin-actions-bar">
  <div className="pm-top-actions-row">
    <div className="pm-left-actions">
      <div className="pm-search-box">
        <img src={SearchIcon} alt="Search" className="pm-search-icon" />
        <input 
          type="text" 
          placeholder="Search" 
          value={searchTerm} 
          onChange={(e) => setSearchTerm(e.target.value)} 
        />
      </div>
      
      <button className="pm-add-patient-button" onClick={handleAddPatient}>
        <svg className="pm-button-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span>Add a Patient</span>
      </button>
    </div>
  </div>
  
  <div className="pm-bottom-actions-row">
    <div className="pm-filter-box">
      <div className="pm-filter-item">
        <label htmlFor="pm-status-filter">Status</label>
        <select 
          id="pm-status-filter" 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="All">All</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Pending">Pending</option>
        </select>
      </div>
      
      <div className="pm-filter-item">
        <label htmlFor="pm-gender-filter">Gender</label>
        <select 
          id="pm-gender-filter" 
          value={genderFilter} 
          onChange={(e) => setGenderFilter(e.target.value)}
        >
          <option value="All">All</option>
          <option value="Male">Male</option>
          <option value="Female">Female</option>
          <option value="Non-Binary">Non-Binary</option>
          <option value="Other">Other</option>
        </select>
      </div>
    </div>
    
    <button className="pm-export-pdf-button" onClick={handleExportPDF}>
      <img src={PdfIcon} alt="Export" className="pm-button-icon" />
      <span>Export PDF</span>
    </button>
  </div>
</div>

      {patients.length === 0 ? (
        <div className="empty-state">
          <h3>No patients found</h3>
          <p>Try adjusting your search or filters, or add a new patient.</p>
        </div>
      ) : (
        <div className="pm-custom-table">
          <div className="pm-table-row pm-header-row">
            <div className="pm-table-cell">Name</div>
            <div className="pm-table-cell">Email</div>
            <div className="pm-table-cell">Registration Date</div>
            <div className="pm-table-cell">Account Status</div>
            <div className="pm-table-cell">Assigned Mental Health Professional</div>
            <div className="pm-table-cell">Actions</div>
          </div>
          
          {patients.map((patient, index) => (
            <div 
              key={`${patient._id}-${patient.tenantId}`}
              className={`pm-table-row ${index % 2 === 0 ? 'pm-odd-row' : 'pm-even-row'}`}
            >
              <div className="pm-table-cell">
                <div className="pm-user-name-cell">
                  <div className="pm-user-avatar">
                    {patient.profilePicture && patient.profilePicture !== 'default-profile.png' && patient.profilePicture.startsWith('http') ? (
                      <img src={patient.profilePicture} alt={`${patient.firstName} ${patient.lastName}`} />
                    ) : (
                      <div className="pm-user-initials">
                        {patient.firstName?.[0]?.toUpperCase()}{patient.lastName?.[0]?.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="pm-user-info">
                    <div className="pm-user-fullname">
                      {patient.firstName} {patient.lastName}
                    </div>
                  </div>
                </div>
              </div>
              <div className="pm-table-cell">{patient.email}</div>
              <div className="pm-table-cell">{formatDate(patient.createdAt)}</div>
              <div className="pm-table-cell">{getStatusBadge(patient.accountStatus)}</div>
              <div className="pm-table-cell">{patient.primaryDoctor || 'Not assigned'}</div>
              <div className="pm-table-cell">
                <div className="pm-action-buttons">
                  <button 
                    className="pm-btn-icon"
                    onClick={() => handleViewPatient(patient._id, patient.tenantId)} 
                    title="View Patient"
                  >
                    <img src={EyeIcon} alt="View" className="pm-action-icon pm-view" />
                  </button>
                  <button 
                    className="pm-btn-icon"
                    onClick={() => handleEditPatient(patient._id, patient.tenantId)} 
                    title="Edit Patient"
                  >
                    <img src={EditIcon} alt="Edit" className="pm-action-icon pm-edit" />
                  </button>
                  <button 
                    className="pm-btn-icon delete-btn"
                    onClick={() => handleDeletePatient(
                      patient._id, 
                      patient.tenantId, 
                      `${patient.firstName} ${patient.lastName}`,
                      patient.email
                    )} 
                    title="Delete Patient"
                  >
                    <img src={TrashIcon} alt="Delete" className="pm-action-icon pm-delete" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="pm-pagination">
  <button 
    className="pm-page-nav" 
    onClick={() => paginate(Math.max(1, currentPage - 1))}
    disabled={currentPage === 1}
  >
    <img src={leftArrow} alt="Previous" />
  </button>
  
  <div className="pm-page-numbers">
    {[...Array(totalPages)].map((_, index) => (
      <button
        key={index}
        className={`pm-page-number ${currentPage === index + 1 ? 'active' : ''}`}
        onClick={() => paginate(index + 1)}
      >
        {index + 1}
      </button>
    ))}
  </div>
  
  <button 
    className="pm-page-nav" 
    onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
    disabled={currentPage === totalPages}
  >
    <img src={rightArrow} alt="Next" />
  </button>
</div>

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDeletePatient}
        patientName={deleteModal.patientName}
        patientEmail={deleteModal.patientEmail}
      />
    </div>
  );
};

export default PatientManagement;